import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import classNames from "classnames"
import { trim } from "lodash"
import { noop } from "lodash-es"
import { AnimatePresence, motion } from "motion/react"
import { create } from "zustand"
import Scroll from "@hg/scroll"

import { useETL, useI18nStore, useTranslation } from "@/locales"
import Input from "@/topic/rogue_5/components/Input"
import Toast from "@/topic/rogue_5/components/Toast"
import { GlobalContext } from "@/topic/rogue_5/context"
import { gameDraw, gameFillAddress } from "@/topic/rogue_5/service"
import { IRogue5GameInfo } from "@/topic/rogue_5/types"
import { useGameStore } from "../../../store"
import MainButton from "../../MainButton"

import styles from "./index.module.less"

enum ELotteryStep {
    NotCompleted = "notCompleted",
    BeforeRoll = "beforeRoll",
    Result = "result",
    Edit = "edit",
    Rule = "rule",
    ConfirmAddress = "confirmAddress",
    ViewAddress = "viewAddress",
}

const useLotteryModalStore = create<{
    activate: (onClose?: () => void) => void
}>(() => ({
    activate: noop,
}))

export const useLotteryModal = () => {
    const { t } = useTranslation()

    return useCallback(
        (onClose?: () => void) => {
            const { info } = useGameStore.getState()

            if (info.roll.cancel) {
                // 如果 cancel 为 true，先显示错误提示
                Toast.message({
                    content:
                        "ご当選から24時間以内にお届け先情報のご記入がなかったため、賞品が【龍門幣】に変更されました。\n" +
                        "※詳細は、抽選規約からご確認ください。",
                    duration: 2000,
                })

                // 使用定时器在 Toast 隐藏后执行操作
                setTimeout(() => {
                    // Toast 消失后调用用户传入的 onClose 回调
                    useLotteryModalStore.getState().activate(onClose)
                }, 2000) // 与 Toast.duration 保持一致
            } else {
                // 如果 cancel 为 false，正常显示弹窗
                useLotteryModalStore.getState().activate(onClose)
            }
        },
        [t]
    )
}

const LotteryModal: React.FC<{
    className?: string
    style?: React.CSSProperties
}> = ({ className, style }) => {
    const { t } = useTranslation()
    const [active, setActive] = useState(false)
    const onCloseRef = useRef<() => void>()
    useEffect(() => {
        useLotteryModalStore.setState({
            activate: (onClose) => {
                setActive(true)
                onCloseRef.current = onClose ?? noop
            },
        })
    }, [])

    const { info: gameInfo, gameType } = useGameStore()
    const [step, setStep] = useState<ELotteryStep>(ELotteryStep.NotCompleted)
    useEffect(() => {
        if (active) {
            if (!gameInfo.game.complete) {
                setStep(ELotteryStep.NotCompleted)
            } else if (gameInfo.roll.used) {
                setStep(ELotteryStep.Result)
            } else {
                setStep(ELotteryStep.BeforeRoll)
            }
        }
        // setStep(ELotteryStep.BeforeRoll)
    }, [active])

    const lockRef = useRef(false)
    const handleClose = useCallback(() => {
        setActive(false)
        onCloseRef.current?.()
    }, [])

    const collect = useETL()
    const handleRoll = useCallback(() => {
        if (lockRef.current) return
        lockRef.current = true
        // todo 静态测试
        // useGameStore.setState((state) => ({
        //     info: {
        //         ...state.info,
        //         roll: {
        //             used: true,
        //             awardId: 1,
        //         },
        //     },
        //     giftEntryActive: true,
        // }))
        // setStep(ELotteryStep.Result)
        // collect?.("lottery", {
        //     target: gameType,
        //     result: 1,
        // })
        // lockRef.current = false
        // return;
        gameDraw()
            .then((res) => {
                if (res.code === 0) {
                    useGameStore.setState((state) => ({
                        info: {
                            ...state.info,
                            roll: {
                                used: true,
                                awardId: res.data.awardId,
                            },
                        },
                        giftEntryActive: true,
                    }))
                    setStep(ELotteryStep.Result)
                    collect?.("lottery", {
                        target: gameType,
                        result: res.data.awardId,
                    })
                } else {
                    if (res.code === 3008) {
                        // 用户等级不足
                        useGameStore.setState({ canRoll: false })
                        // 等级不足时不做关闭回调
                        setActive(false)
                    }
                    Toast.message({ content: res.msg })
                }
                lockRef.current = false
            })
            .catch((err) => {
                lockRef.current = false
                console.log(err)
            })
    }, [collect, gameType])

    const {
        state: { user },
    } = useContext(GlobalContext)
    const { sdkReady } = useI18nStore()
    const [rawContact, setContact] = useState<IRogue5GameInfo["contact"]>()
    const contact = useMemo(() => {
        return gameInfo.contact ?? rawContact
    }, [gameInfo.contact, rawContact])
    const handleFillAddress = useCallback(() => {
        if (contact?.name && contact.phone && contact.address) {
            setStep(ELotteryStep.ConfirmAddress)
        } else {
            setStep(ELotteryStep.Edit)
        }
    }, [sdkReady, user?.channelId])

    // --------------------------
    const [formDisabled, setFormDisabled] = useState<any>(false)
    const [inputAddress, setInputAddress] = useState<any>({})
    const [loading, setLoading] = useState(false)
    const handleInputName = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setInputAddress((prev: any) => ({
                ...prev,
                name: trim(e.target.value).slice(0, 32),
            }))
        },
        []
    )
    const handleInputPhone = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const cleanedValue = e.target.value.replace(/\D/g, "").slice(0, 11)
            setInputAddress((prev: any) => ({
                ...prev,
                phone: cleanedValue.trim(),
            }))
        },
        []
    )
    const handleInputEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setInputAddress((prev: any) => ({
            ...prev,
            email: trim(value),
        }))
    }

    const handleInputpostcode = (e: React.ChangeEvent<HTMLInputElement>) => {
        const cleanedValue = e.target.value.replace(/\D/g, "").slice(0, 7)
        setInputAddress((prev: any) => ({
            ...prev,
            postcode: cleanedValue.trim(),
        }))
    }

    const handleChangeAddr = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const value = e.target.value
            setInputAddress((prev: any) => ({
                ...prev,
                address: value.slice(0, 150),
            }))
        },
        []
    )
    // 添加邮箱格式校验函数
    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }
    const validatePhoneNumber = (phone: string): boolean => {
        // 检查是否为10 or 11位数字
        const phoneRegex = /^\d{10,11}$/
        return phoneRegex.test(phone)
    }
    const validatePostcode = (postcode: string): boolean => {
        const postcodeRegex = /^\d{7}$/
        return postcodeRegex.test(postcode)
    }

    const handleViewAddress = useCallback(() => {
        if (!inputAddress.name) {
            Toast.message({ content: t("game.lottery.warn.name") })
            return
        } else if (!inputAddress.phone) {
            Toast.message({ content: t("game.lottery.warn.phone") })
            return
        } else if (!inputAddress.email) {
            Toast.message({ content: t("game.lottery.warn.email") })
            return
        } else if (!inputAddress.postcode) {
            Toast.message({ content: t("game.lottery.warn.postcode") })
            return
        } else if (!inputAddress.address) {
            Toast.message({ content: t("game.lottery.warn.address") })
            return
        } else if (!validatePhoneNumber(inputAddress.phone)) {
            Toast.message({ content: t("game.lottery.warn.phone2") })
            return
        } else if (!validateEmail(inputAddress.email)) {
            Toast.message({ content: t("game.lottery.warn.email2") })
            return
        } else if (!validatePostcode(inputAddress.postcode)) {
            Toast.message({ content: t("game.lottery.warn.postcode2") })
            return
        }
        setContact(inputAddress)
        setStep(ELotteryStep.ConfirmAddress)
    }, [inputAddress, loading])

    const handleConfirmAddress = useCallback(() => {
        lockRef.current = true
        gameFillAddress(inputAddress)
            .then((res) => {
                if (res.code === 0) {
                    useGameStore.setState((state) => ({
                        info: {
                            ...state.info,
                            contact: inputAddress,
                        },
                    }))
                    handleClose()
                } else {
                    Toast.message({ content: res.msg })
                }
                lockRef.current = false
            })
            .catch((err) => {
                lockRef.current = false
                console.log(err)
                Toast.message({ content: err })
            })
    }, [inputAddress])

    return (
        <div
            className={classNames(
                styles.modalContainer,
                active && styles.active,
                className
            )}
            // onClick={() => {
            //     setActive(false)
            // }}
            style={style}
        >
            <div
                className={styles.lotteryContainer}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className={classNames(styles.title, styles["normal"])}
                ></div>
                <div
                    className={classNames(
                        styles.title,
                        styles["gift"],
                        styles.active
                    )}
                >
                    {step === ELotteryStep.Rule ||
                    step === ELotteryStep.BeforeRoll
                        ? t("game.lottery.ruleTitle")
                        : t("game.lottery.title")}
                </div>
                <AnimatePresence mode="wait">
                    {/* Edit form */}
                    {step === ELotteryStep.Edit && (
                        <motion.div
                            key="edit"
                            className={styles.editFrame}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                            <div className={styles.giftFrame}>
                                <div
                                    className={classNames(
                                        styles.coin,
                                        styles.left
                                    )}
                                ></div>
                                <div
                                    className={classNames(
                                        styles.coin,
                                        styles.right
                                    )}
                                ></div>
                                <div
                                    className={styles.giftImage}
                                    data-key={gameInfo.roll.awardId}
                                ></div>

                                <div className={styles.giftName}>
                                    {t(
                                        `game.lottery.gift.${gameInfo.roll.awardId}`
                                    )}
                                </div>
                            </div>
                            <div className={styles.form}>
                                <Scroll
                                    direction="y"
                                    className={styles.formScroll}
                                    rotateDetection
                                >
                                    <div className={styles.flex}>
                                        <Input
                                            className={styles.name}
                                            value={inputAddress.name}
                                            onChange={handleInputName}
                                            disabled={formDisabled}
                                            aria-label="full name"
                                            placeholder={t(
                                                "game.lottery.form.name"
                                            )}
                                        />
                                        <Input
                                            className={styles.phone}
                                            value={inputAddress.phone}
                                            onChange={handleInputPhone}
                                            disabled={formDisabled}
                                            aria-label="phone"
                                            placeholder={t(
                                                "game.lottery.form.telephone"
                                            )}
                                        />
                                    </div>
                                    <div className={styles.flex}>
                                        <Input
                                            className={styles.email}
                                            value={inputAddress.email}
                                            onChange={handleInputEmail}
                                            disabled={formDisabled}
                                            aria-label="email"
                                            placeholder={t(
                                                "game.lottery.form.email"
                                            )}
                                        />
                                        <Input
                                            className={styles.postcode}
                                            value={inputAddress.postcode}
                                            onChange={handleInputpostcode}
                                            disabled={formDisabled}
                                            aria-label="postcode"
                                            placeholder={t(
                                                "game.lottery.form.postcode"
                                            )}
                                        />
                                    </div>
                                    <textarea
                                        className={styles.address}
                                        value={inputAddress.address}
                                        onChange={handleChangeAddr}
                                        disabled={formDisabled}
                                        aria-label="addr"
                                        placeholder={t(
                                            "game.lottery.form.address"
                                        )}
                                    />
                                </Scroll>
                            </div>
                        </motion.div>
                    )}
                    {/* result frame */}
                    {step === ELotteryStep.Result && (
                        <motion.div
                            key="result"
                            className={styles.giftFrame}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                            <div
                                className={classNames(styles.coin, styles.left)}
                            ></div>
                            <div
                                className={classNames(
                                    styles.coin,
                                    styles.right
                                )}
                            ></div>
                            <div
                                className={styles.giftImage}
                                data-key={gameInfo.roll.awardId}
                            ></div>

                            <div className={styles.giftName}>
                                {t(
                                    `game.lottery.gift.${gameInfo.roll.awardId}`
                                )}
                            </div>
                        </motion.div>
                    )}
                    {/* rule */}
                    {(step === ELotteryStep.Rule ||
                        step === ELotteryStep.BeforeRoll) && (
                        <motion.div
                            key="rule"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                duration: 0.3,
                                ease: "easeInOut",
                            }}
                        >
                            <Scroll
                                direction="y"
                                rotateDetection
                                className={styles.lotteryRule}
                            >
                                {t("game.lottery.rule")
                                    .split("\n")
                                    .map((line, index) => (
                                        <li className={styles.rule} key={index}>
                                            <div className={styles.index}>
                                                {index + 1}
                                            </div>
                                            <div
                                                className={styles.content}
                                                dangerouslySetInnerHTML={{
                                                    __html: line,
                                                }}
                                            />
                                        </li>
                                    ))}
                            </Scroll>
                        </motion.div>
                    )}
                    {/* not completed */}
                    {step === ELotteryStep.NotCompleted && (
                        <motion.div
                            key="notCompleted"
                            className={styles.giftFrame}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <div
                                className={classNames(styles.coin, styles.left)}
                            ></div>
                            <div
                                className={classNames(
                                    styles.coin,
                                    styles.right
                                )}
                            ></div>
                            <div className={styles.text}>
                                {t("game.lottery.notCompleted")
                                    .split("\n")
                                    .map((line, index) => (
                                        <div
                                            className={styles.line}
                                            key={index}
                                        >
                                            {line}
                                        </div>
                                    ))}
                            </div>
                        </motion.div>
                    )}
                    {/* view address */}
                    {(step === ELotteryStep.ViewAddress ||
                        step === ELotteryStep.ConfirmAddress) && (
                        <motion.div
                            key="viewAddress"
                            className={styles.viewAddress}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <Scroll
                                direction="y"
                                className={styles.container}
                                rotateDetection
                            >
                                <div className={styles.noticeText}>
                                    {(step === ELotteryStep.ViewAddress
                                        ? t("game.lottery.viewAddress")
                                        : t("game.lottery.award")
                                    )
                                        .split("\n")
                                        .map((line, index) => (
                                            <div
                                                className={styles.line}
                                                key={index}
                                            >
                                                {line.replace(
                                                    "$0",
                                                    t(
                                                        `game.lottery.gift.${gameInfo.roll.awardId}`
                                                    )
                                                )}
                                            </div>
                                        ))}
                                </div>
                                <div className={styles.addressTitle}>
                                    {t("game.lottery.notice")}
                                </div>
                                <div className={styles.addressItem}>
                                    <div className={styles.label}>
                                        {t("game.lottery.form.name")}
                                    </div>
                                    <div className={styles.value}>
                                        {contact?.name}
                                    </div>
                                </div>
                                <div className={styles.addressItem}>
                                    <div className={styles.label}>
                                        {t("game.lottery.form.telephone")}
                                    </div>
                                    <div className={styles.value}>
                                        {contact?.phone}
                                    </div>
                                </div>
                                <div className={styles.addressItem}>
                                    <div className={styles.label}>
                                        {t("game.lottery.form.email")}
                                    </div>
                                    <div className={styles.value}>
                                        {contact?.email}
                                    </div>
                                </div>
                                <div className={styles.addressItem}>
                                    <div className={styles.label}>
                                        {t("game.lottery.form.postcode")}
                                    </div>
                                    <div className={styles.value}>
                                        {contact?.postcode}
                                    </div>
                                </div>
                                <div
                                    className={classNames(
                                        styles.addressItem,
                                        styles.area
                                    )}
                                >
                                    <div className={styles.label}>
                                        {t("game.lottery.form.address")}
                                    </div>
                                    <Scroll
                                        direction="y"
                                        className={styles.value}
                                        rotateDetection
                                    >
                                        {contact?.address}
                                    </Scroll>
                                </div>
                            </Scroll>
                        </motion.div>
                    )}
                </AnimatePresence>
                <AnimatePresence mode="wait">
                    {active && (
                        <motion.div
                            key={step}
                            className={styles.buttonGroup}
                            initial={{ opacity: 0, pointerEvents: "none" }}
                            animate={{ opacity: 1, pointerEvents: "auto" }}
                            exit={{ opacity: 0, pointerEvents: "none" }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                            {step === ELotteryStep.Edit && (
                                <MainButton
                                    type="pink"
                                    text={t("game.lottery.button.next.cn")}
                                    textEn={t("game.lottery.button.next.en")}
                                    mark={null}
                                    icon={"flower"}
                                    onClick={handleViewAddress}
                                />
                            )}
                            {step === ELotteryStep.BeforeRoll && (
                                <MainButton
                                    type="pink"
                                    text={t("game.lottery.button.roll.cn")}
                                    textEn={t("game.lottery.button.roll.en")}
                                    mark={null}
                                    icon={"gold"}
                                    onClick={handleRoll}
                                />
                            )}
                            {step === ELotteryStep.Result &&
                                (gameInfo.roll.awardId !== "gold" ? (
                                    !gameInfo.contact ? (
                                        <>
                                            <MainButton
                                                type="yellow"
                                                text={t(
                                                    "game.lottery.button.confirm.cn"
                                                )}
                                                textEn={t(
                                                    "game.lottery.button.confirm.en"
                                                )}
                                                mark={null}
                                                icon={"flower"}
                                                onClick={handleClose}
                                            />
                                            <MainButton
                                                type="blue"
                                                text={t(
                                                    "game.lottery.button.fillAddress.cn"
                                                )}
                                                textEn={t(
                                                    "game.lottery.button.fillAddress.en"
                                                )}
                                                mark={null}
                                                icon={"temple"}
                                                onClick={handleFillAddress}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <MainButton
                                                type="blue"
                                                text={t(
                                                    "game.lottery.button.viewAddress.cn"
                                                )}
                                                textEn={t(
                                                    "game.lottery.button.viewAddress.en"
                                                )}
                                                mark={null}
                                                icon={"temple"}
                                                onClick={() => {
                                                    if (lockRef.current) return
                                                    setStep(
                                                        ELotteryStep.ViewAddress
                                                    )
                                                }}
                                            />
                                            <MainButton
                                                type="yellow"
                                                text={t(
                                                    "game.lottery.button.confirm.cn"
                                                )}
                                                textEn={t(
                                                    "game.lottery.button.confirm.en"
                                                )}
                                                mark={null}
                                                icon={"flower"}
                                                onClick={handleClose}
                                            />
                                        </>
                                    )
                                ) : (
                                    <MainButton
                                        type="yellow"
                                        text={t(
                                            "game.lottery.button.confirm.cn"
                                        )}
                                        textEn={t(
                                            "game.lottery.button.confirm.en"
                                        )}
                                        mark={null}
                                        icon={"flower"}
                                        onClick={handleClose}
                                    />
                                ))}
                            {/*确认信息并提交*/}
                            {step === ELotteryStep.ConfirmAddress && (
                                <>
                                    <MainButton
                                        type="blue"
                                        text={t(
                                            "game.lottery.button.changeAddress.cn"
                                        )}
                                        textEn={t(
                                            "game.lottery.button.changeAddress.en"
                                        )}
                                        mark={null}
                                        icon={"temple"}
                                        onClick={() => {
                                            if (lockRef.current) return
                                            setStep(ELotteryStep.Result)
                                        }}
                                    />
                                    <MainButton
                                        type="yellow"
                                        text={t(
                                            "game.lottery.button.confirmAddress.cn"
                                        )}
                                        textEn={t(
                                            "game.lottery.button.confirmAddress.en"
                                        )}
                                        mark={null}
                                        icon={"flower"}
                                        onClick={handleConfirmAddress}
                                    />
                                </>
                            )}
                            {/*仅仅查看信息*/}
                            {step === ELotteryStep.ViewAddress && (
                                <>
                                    <MainButton
                                        type="pink"
                                        text={t("game.lottery.button.back.cn")}
                                        textEn={t(
                                            "game.lottery.button.back.en"
                                        )}
                                        mark={null}
                                        icon={"back"}
                                        onClick={() => {
                                            if (lockRef.current) return
                                            setStep(ELotteryStep.Result)
                                        }}
                                    />
                                    <MainButton
                                        type="yellow"
                                        text={t(
                                            "game.lottery.button.confirm.cn"
                                        )}
                                        textEn={t(
                                            "game.lottery.button.confirm.en"
                                        )}
                                        mark={null}
                                        icon={"flower"}
                                        onClick={handleClose}
                                    />
                                </>
                            )}
                            {step === ELotteryStep.NotCompleted && (
                                <MainButton
                                    type="yellow"
                                    text={t("game.lottery.button.confirm.cn")}
                                    textEn={t("game.lottery.button.confirm.en")}
                                    mark={null}
                                    icon={"flower"}
                                    onClick={handleClose}
                                />
                            )}
                            {step === ELotteryStep.Rule && (
                                <MainButton
                                    type="pink"
                                    text={t("game.lottery.button.back.cn")}
                                    textEn={t("game.lottery.button.back.en")}
                                    mark={null}
                                    icon={"back"}
                                    onClick={() => {
                                        setStep(ELotteryStep.Result)
                                    }}
                                />
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
                <MainButton
                    className={classNames(
                        styles.ruleButton,
                        [ELotteryStep.Result].includes(step) && styles.active
                    )}
                    type="small"
                    text={t("game.lottery.button.rule.cn")}
                    textEn={t("game.lottery.button.rule.en")}
                    mark={null}
                    icon={"rule"}
                    onClick={() => {
                        setStep(ELotteryStep.Rule)
                    }}
                />

                {/* <div className={styles.close}></div> */}
            </div>
        </div>
    )
}

export default LotteryModal
